import React, { useState } from "react";
import { Download, FileSpreadsheet, BarChart2, TrendingDown, Archive, AlertOctagon } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

const REPORT_TYPES = [
  { key: "utilisation",  label: "Leave Utilisation",  icon: BarChart2,     desc: "Who used the most/least leave by type and dept"     },
  { key: "lop",          label: "LOP Report",         icon: TrendingDown,  desc: "Employees who went into negative balance"           },
  { key: "carry_over",   label: "Carry-Over Report",  icon: Archive,       desc: "Unused leave being carried into next year"          },
  { key: "absenteeism",  label: "Absenteeism Trends", icon: AlertOctagon,  desc: "Unplanned absence patterns by dept/location/month"  },
];

export default function ReportsTab() {
  const [reportType, setReportType] = useState("utilisation");
  const [filters, setFilters] = useState({
    from: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    to:   new Date().toISOString().slice(0, 10),
    dept: "ALL", location: "ALL", leave_type: "ALL",
  });

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [exporting, setExporting] = useState(null);

  const token = localStorage.getItem("token");
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const run = async () => {
    setLoading(true); setError(null); setData(null);
    try {
      const qs = new URLSearchParams({ ...filters, report: reportType }).toString();
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/reports?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const qs = new URLSearchParams({ ...filters, report: reportType, format }).toString();
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/reports/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext  = format === "excel" ? "xlsx" : "csv";
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `leave_${reportType}_${filters.from}_${filters.to}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExporting(null); }
  };

  const rows    = data?.rows    || [];
  const summary = data?.summary || null;
  const chart   = data?.chart   || [];

  return (
    <div className="space-y-5">

      {/* Report type selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {REPORT_TYPES.map(({ key, label, icon: Icon, desc }) => (
          <button key={key} onClick={() => { setReportType(key); setData(null); }}
            className={`text-left rounded-xl border p-4 transition-all ${
              reportType === key
                ? "border-[#00ff90]/40 bg-[#00ff90]/8 shadow-[0_0_12px_#00ff9015]"
                : "border-white/5 bg-white/3 hover:border-[#00ff90]/20 hover:bg-[#00ff90]/5"
            }`}>
            <Icon size={16} className={reportType === key ? "text-[#00ff90]" : "text-white/30"} />
            <p className={`text-sm font-medium mt-2 ${reportType === key ? "text-[#00ff90]" : "text-white/60"}`}>{label}</p>
            <p className="text-xs text-white/25 mt-0.5 leading-relaxed">{desc}</p>
          </button>
        ))}
      </div>

      {/* Filters + run */}
      <div className={`${card} p-5`}>
        <div className="flex flex-wrap gap-3 items-end">
          <div><label className={labelCls}>From</label>
            <input type="date" className={inputCls} value={filters.from} onChange={e => set("from", e.target.value)} /></div>
          <div><label className={labelCls}>To</label>
            <input type="date" className={inputCls} value={filters.to} onChange={e => set("to", e.target.value)} /></div>
          <div><label className={labelCls}>Department</label>
            <select className={inputCls} value={filters.dept} onChange={e => set("dept", e.target.value)}>
              {["ALL","Engineering","Product","QA","HR","Ops"].map(d => <option key={d} value={d}>{d === "ALL" ? "All Depts" : d}</option>)}
            </select></div>
          <div><label className={labelCls}>Location</label>
            <select className={inputCls} value={filters.location} onChange={e => set("location", e.target.value)}>
              {["ALL","London","Bangalore","Remote"].map(l => <option key={l} value={l}>{l === "ALL" ? "All Locations" : l}</option>)}
            </select></div>
          <div><label className={labelCls}>Leave Type</label>
            <select className={inputCls} value={filters.leave_type} onChange={e => set("leave_type", e.target.value)}>
              {["ALL","Annual","Sick","Casual","Unpaid"].map(t => <option key={t} value={t}>{t === "ALL" ? "All Types" : t}</option>)}
            </select></div>
          <button onClick={run} disabled={loading}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium self-end">
            {loading ? "Running…" : "Run Report"}
          </button>
          {data && (
            <>
              <button onClick={() => exportReport("csv")} disabled={exporting !== null}
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors self-end">
                <Download size={12} /> CSV
              </button>
              <button onClick={() => exportReport("excel")} disabled={exporting !== null}
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors self-end">
                <FileSpreadsheet size={12} /> Excel
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Results */}
      {data && (
        <div className="space-y-5">

          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-white/5 bg-white/3 p-4 text-center">
                  <p className="text-xl font-bold text-[#00ff90]">{v}</p>
                  <p className="text-xs text-white/30 mt-0.5 capitalize">{k.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          )}

          {/* Chart */}
          {chart.length > 0 && (
            <div className={`${card} p-5`}>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-3">
                {REPORT_TYPES.find(r => r.key === reportType)?.label} Chart
              </p>
              <ResponsiveContainer width="100%" height={180}>
                {reportType === "absenteeism" ? (
                  <LineChart data={chart} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                    <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                    <Tooltip {...tooltipStyle} />
                    <Line type="monotone" dataKey="count" stroke="#f87171" strokeWidth={2} dot={{ fill: "#f87171", r: 3 }} />
                  </LineChart>
                ) : (
                  <BarChart data={chart} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                    <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {chart.map((_, i) => <Cell key={i} fill="#00ff90" fillOpacity={0.7 - i * 0.03} />)}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          {rows.length > 0 && (
            <div className={card}>
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
                  {REPORT_TYPES.find(r => r.key === reportType)?.label}
                </span>
                <span className="text-xs text-white/30">{rows.length} rows</span>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#0d0d0d]">
                    <tr className="border-b border-white/5">
                      {Object.keys(rows[0]).map(k => (
                        <th key={k} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium whitespace-nowrap">
                          {k.replace(/_/g, " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                        {Object.values(r).map((v, j) => (
                          <td key={j} className="px-3 py-2.5 text-white/60 text-xs whitespace-nowrap">{String(v ?? "—")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}